import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Switch, Tooltip } from '@mui/material';
import dayjs from 'dayjs';
import { warning } from '../util/Alert';
import {
  getStaff,
  openStaffDialog,
  toggleStaffStatus,
  deleteStaff,
} from '../store/staff/action';
import { getRoles as fetchRoles } from '../store/role/action';
import StaffDialog from '../component/dialog/StaffDialog';
import { usePermission } from '../component/context/PermissionProvider';
import Pagination from './Pagination';

const StaffTable = () => {
  const dispatch = useDispatch();
  const { staff, total } = useSelector((state) => state.staff);
  const { can } = usePermission();
  const canCreate = can('admin/staff', 'Create');
  const canEdit = can('admin/staff', 'Edit');
  const canDelete = can('admin/staff', 'Delete');
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activePage, setActivePage] = useState(1);

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getStaff({ start: page, limit: rowsPerPage }));
  }, [dispatch, page, rowsPerPage]);

  const handlePageChange = (pageNumber) => {
    setActivePage(pageNumber);
    setPage(pageNumber);
  };

  const handleRowsPerPage = (value) => {
    setActivePage(1);
    setRowsPerPage(value);
  };

  useEffect(() => {
    setData(staff);
  }, [staff]);

  const handleSearch = (e) => {
    const value = (e.target.value || '').trim().toUpperCase();
    if (value) {
      setData(
        staff.filter(
          (s) =>
            s?.name?.toUpperCase?.()?.indexOf(value) > -1 ||
            s?.email?.toUpperCase?.()?.indexOf(value) > -1
        )
      );
    } else {
      setData(staff);
    }
  };

  const handleDelete = (subadminId) => {
    warning().then((isDeleted) => {
      if (isDeleted) {
        dispatch(deleteStaff(subadminId));
      }
    }).catch((err) => console.log(err));
  };

  const handleEdit = (row) => {
    dispatch(openStaffDialog(row));
  };

  const handleCreate = () => {
    dispatch(openStaffDialog());
  };

  const handleToggleStatus = (row) => {
    dispatch(toggleStaffStatus(row._id));
  };

  const getRoleName = (row) => {
    if (row?.role?.name) return row.role.name;
    if (row?.roleId) return row.roleId;
    return '–';
  };

  return (
    <>
      <div className="page-title">
        <div className="row">
          <div className="col-12 col-md-6 order-md-1 order-last">
            <h3 className="mb-3 text-dark">Staff</h3>
          </div>
          <div className="col-12 col-md-6 order-md-2 order-first">
            <nav
              aria-label="breadcrumb"
              className="breadcrumb-header float-start float-lg-end"
            >
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/admin/dashboard" className="text-danger">
                    Dashboard
                  </Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Staff
                </li>
              </ol>
            </nav>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col">
          <div className="card">
            <div className="card-header pb-0">
              <div className="row my-3">
                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-8 float-left">
                  {canCreate && (
                    <button
                      type="button"
                      className="btn waves-effect waves-light btn-danger btn-sm float-left"
                      onClick={handleCreate}
                    >
                      <i className="fa fa-plus"></i>
                      <span className="icon_margin">New</span>
                    </button>
                  )}
                </div>
                <div className="col-xs-12 col-sm-12 col-md-6 col-lg-4 float-right mt-3 mt-lg-0 mt-xl-0">
                  <form action="">
                    <div className="input-group mb-3 border rounded-pill">
                      <div className="input-group-prepend border-0">
                        <div id="button-addon4" className="btn text-danger">
                          <i className="fas fa-search mt-2"></i>
                        </div>
                      </div>
                      <input
                        type="search"
                        placeholder="Search by name or email..."
                        aria-describedby="button-addon4"
                        className="form-control bg-none border-0 rounded-pill searchBar"
                        onChange={handleSearch}
                      />
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="card-body card-overflow">
              <table className="table table-striped">
                <thead className="text-dark">
                  <tr>
                    <th>No.</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Created</th>
                    <th>Updated</th>
                    <th>Status</th>
                    {(canEdit || canDelete) && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody className="text-center">
                  {data?.length > 0 ? (
                    data.map((row, index) => (
                      <tr key={row._id || index}>
                        <td>{index + 1}</td>
                        <td>{row.name || '–'}</td>
                        <td>{row.email || '–'}</td>
                        <td>{getRoleName(row)}</td>
                        <td>
                          {row.createdAt
                            ? dayjs(row.createdAt).format('DD MMM, YYYY')
                            : '–'}
                        </td>
                        <td>
                          {row.updatedAt
                            ? dayjs(row.updatedAt).format('DD MMM, YYYY')
                            : '–'}
                        </td>
                        <td>
                          <Switch
                            checked={!!row.isActive}
                            onChange={() => handleToggleStatus(row)}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: '#FFD700',
                              },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                backgroundColor: '#FFD700',
                              },
                            }}
                          />
                        </td>
                        <td>
                          <div className="d-flex justify-content-center gap-2">
                            {canEdit && (
                              <Tooltip title="Edit">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary mr-1"
                                  onClick={() => handleEdit(row)}
                                >
                                  <i className="fa fa-edit"></i>
                                </button>
                              </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title="Delete">
                                <button
                                  type="button"
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(row._id)}
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} align="center">
                        Nothing to show!!
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <Pagination
        activePage={activePage}
        rowsPerPage={rowsPerPage}
        userTotal={total}
        handleRowsPerPage={handleRowsPerPage}
        handlePageChange={handlePageChange}
      />
      <StaffDialog />
    </>
  );
};

export default StaffTable;
